'use client';
import { limitWorkspaceDirectories, MAX_WORKSPACE_DIRECTORIES, } from '#pplx/frontend/apps/ask/ask-input/ask-input-types/index.js';
import { MAX_COMPARE_MODELS, SearchMode, SearchModel, } from '#pplx/frontend/ask-models/types/search-modes.js';
import { SafeSessionStorage } from '#pplx/frontend/browser-storage/sessionStorage.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { createZustandContext } from '#pplx/frontend/zustand-context/createZustandContext.js';
import { createRef } from 'react';
import { create } from 'zustand';
function hydrateDraft(queryDraftKey) {
    if (!queryDraftKey)
        return {};
    const raw = SafeSessionStorage.getItem(queryDraftKey);
    if (!raw)
        return {};
    try {
        return JSON.parse(raw);
    }
    catch {
        return {};
    }
}
function persistDraft(draft, queryDraftKey) {
    if (!queryDraftKey)
        return;
    SafeSessionStorage.setItem(queryDraftKey, JSON.stringify(draft));
}
function clearDraft(queryDraftKey) {
    if (!queryDraftKey)
        return;
    SafeSessionStorage.removeItem(queryDraftKey);
}
const INITIAL_BLANK_STATE_SUGGESTIONS = {
    [SearchMode.SEARCH]: null,
    [SearchMode.RESEARCH]: null,
    [SearchMode.STUDY]: null,
    [SearchMode.AGENTIC_RESEARCH]: null,
    [SearchMode.BROWSER_AGENT]: null,
    [SearchMode.ASI]: null,
};
function createDraftActions(set, get, queryDraftKey) {
    return {
        setUserInput: (value, json) => {
            const askDeepResearchSelection = value ? get().askDeepResearchSelection : undefined;
            set({ userInput: value, userInputJson: json, askDeepResearchSelection });
            persistDraft({ rawQuery: value, rawQueryJson: json, askDeepResearchSelection }, queryDraftKey);
        },
        setAskDeepResearchDraftSelection: (askDeepResearchSelection) => {
            set({ askDeepResearchSelection });
            const { userInput, userInputJson } = get();
            persistDraft({ rawQuery: userInput, rawQueryJson: userInputJson, askDeepResearchSelection }, queryDraftKey);
        },
        setHasStartedTyping: (value) => set({ hasStartedTyping: value }),
        setStartTypingTime: (time) => set({ startTypingTime: time }),
        resetInput: () => {
            set({ userInput: '', userInputJson: undefined, askDeepResearchSelection: undefined });
            clearDraft(queryDraftKey);
        },
        markInputAsSubmitted: () => {
            set({
                userInput: '',
                userInputJson: undefined,
                askDeepResearchSelection: undefined,
                startTypingTime: undefined,
            });
        },
        restoreInput: () => {
            const draft = hydrateDraft(queryDraftKey);
            if (draft.rawQuery) {
                set({
                    userInput: draft.rawQuery,
                    userInputJson: draft.rawQueryJson,
                    askDeepResearchSelection: draft.askDeepResearchSelection,
                });
            }
        },
        setShouldTriggerFocus: (value) => set({ shouldTriggerFocus: value }),
    };
}
function createUIActions(set, get) {
    return {
        onActiveMenuChange: (menu) => set({ activeMenu: menu }),
        closeActiveMenuIfCurrent: (menu) => {
            if (get().activeMenu === menu) {
                set({ activeMenu: null });
            }
        },
        setFileUploadUpsellTooltipOpen: (open) => set({ fileUploadUpsellTooltipOpen: open }),
        setSuggestions: (suggestions, unsetOnUnfocus = false) => {
            set({ suggestions, shouldUnsetSuggestionsOnUnfocus: unsetOnUnfocus });
        },
        setBlankStateSuggestions: (suggestions, searchMode) => {
            set({
                blankStateSuggestions: { ...get().blankStateSuggestions, [searchMode]: suggestions },
            });
        },
        setShowSuggestDropdown: (show) => set({ showSuggestDropdown: show }),
        setAttachments: (attachments) => set({ attachments }),
    };
}
function createPerQueryActions(set) {
    return {
        setBrowserAgentAllowOnce: (enabled) => set({ browserAgentAllowOnceFromToggle: enabled }),
        setForceEnableBrowserAgent: (enabled) => set({ forceEnableBrowserAgent: enabled }),
        setSources: (sources) => set({ sources }),
        setConfiguredModel: (model) => set({ configuredModel: model }),
        setCompareModels: (models) => set({ compareModels: models.slice(0, MAX_COMPARE_MODELS) }),
        setExhaustedSearchMode: (mode) => set({ exhaustedSearchMode: mode }),
    };
}
function createWorkspaceActions(set, get) {
    return {
        addWorkspaceDirectory: (dir) => {
            const { workspaceDirectories: current } = get();
            if (current.length >= MAX_WORKSPACE_DIRECTORIES)
                return;
            if (current.some((d) => d.path === dir.path))
                return;
            set({ workspaceDirectories: [...current, dir] });
        },
        removeWorkspaceDirectory: (path) => {
            const { workspaceDirectories: current } = get();
            set({ workspaceDirectories: current.filter((d) => d.path !== path) });
        },
        setWorkspaceDirectories: (dirs) => set({ workspaceDirectories: limitWorkspaceDirectories(dirs) }),
    };
}
export const dest = createZustandContext('AskInputRootContext', (initial) => create((set, get) => {
    // Hydrate the draft synchronously at store creation. This is a pure SPA
    // (ReactDOM.createRoot, no SSR), so sessionStorage is available and reading
    // it here avoids a flash of empty input on mount.
    const hydrated = hydrateDraft(initial.queryDraftKey);
    return {
        userInput: hydrated.rawQuery ?? '',
        userInputJson: hydrated.rawQueryJson,
        askDeepResearchSelection: hydrated.askDeepResearchSelection,
        hasStartedTyping: false,
        startTypingTime: undefined,
        shouldTriggerFocus: false,
        activeMenu: null,
        inputRef: createRef(),
        fileUploadUpsellTooltipOpen: false,
        suggestions: EMPTY_ARRAY,
        blankStateSuggestions: { ...INITIAL_BLANK_STATE_SUGGESTIONS },
        showSuggestDropdown: false,
        shouldUnsetSuggestionsOnUnfocus: false,
        browserAgentAllowOnceFromToggle: false,
        attachments: undefined,
        forceEnableBrowserAgent: false,
        workspaceDirectories: limitWorkspaceDirectories(initial.workspaceDirectories),
        sources: initial.defaultSources ?? ['web'],
        configuredModel: initial.defaultSearchModel ?? SearchModel.DEFAULT,
        compareModels: initial.defaultCompareModels ?? [],
        exhaustedSearchMode: undefined,
        actions: {
            ...createDraftActions(set, get, initial.queryDraftKey),
            ...createUIActions(set, get),
            ...createPerQueryActions(set),
            ...createWorkspaceActions(set, get),
        },
    };
}));
export const useAskInputRootState = dest.useTrackedState;
export const useAskInputRootActions = () => dest.useSelector((state) => state.actions);
export const useAskInputRootStore = dest.useStore;
//# sourceMappingURL=AskInputRootShared.js.map